import React from "react";
import {Chip, Tooltip} from "@mui/material";
import Loader from "../../../../common/components/shared/loader/loader";
import Table, {TableActionFunction} from "../../../../common/components/shared/table/table";
import {Api} from "../../../../helpers/api";
import i18n from "../../../../i18n/i18n";
import {DateUtils} from "../../../../helpers/dateUtils";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {NavLink} from "react-router-dom";
import {routingAddresses} from "../../../../helpers/routing";
import "../../transportation.scss";
import {OrderModel, OrderStatusEnum} from "../../../models/order";
import ReloadButton from "../../../../common/components/shared/reloadButton/reloadButton";
import {storesContext} from "../../../../common/stores/stores";
import {IPaginationStore} from "../../../../common/stores/paginationStore";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PopperMenu from "../../../../common/components/shared/popperMenu/popperMenu";
import MenuItem from "@mui/material/MenuItem";
import {IUserStore} from "../../../../administration/stores/userStore";
import Button from "../../../../common/components/shared/button/button";
import {OrderService} from "../../../services/orderService";
import TransportUtils from "../../../../helpers/transportUtils";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import {RelationType} from "../../../models/relation";
import TransportationFilters from "../../transportationFilters/transportationFilters";
import WarningFiltersPopup from "../../../../common/components/shared/warningFiltersPopup/warningFiltersPopup";
import Validator from "../../../../helpers/validator";
import {SortDirection} from "../../../../common/models/table";
import DownloadPrintButton from "./downloadPrintButton/downloadPrintButton";
import SearchButton from "../../../../common/components/shared/searchButton/searchButton";

interface OrderListState {
    orderData: OrderModel[] | undefined | null;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    currentPage: number;
    menuIsOpen: boolean;
    openMenuId: string | null;
    anchorMenu: HTMLElement | null;
    isTableLoader: boolean;
    selectedDateOfRegistrationFrom: ValidatedValue<Date | null>;
    selectedDateOfRegistrationTo: ValidatedValue<Date | null>;
    selectedArrivalDepartureTime: ValidatedValue<Date | null>;
    selectedFerry: ValidatedValue<string>;
    selectedOrderStatus: ValidatedValue<OrderStatusEnum | "">;
    selectedRelationType: ValidatedValue<RelationType | "">;
    displayPopup: boolean;
    orderBy: string | undefined;
    sortDirection: SortDirection | undefined;
    downloadFileResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    cancelOrderResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    isLoading: boolean;
    searchInput: ValidatedValue<string>;
    isSearchLoader: boolean;
}

export default class OrderList extends React.Component<any, OrderListState> {
    static contextType = storesContext;

    private sortableElements = ["shipownerName", "ferry", "arrivalDepartureTime", "trailerNo", "regNo"];

    constructor(props: any) {
        super(props);

        const currentDataWithTime = new Date();
        currentDataWithTime.setMonth(currentDataWithTime.getMonth() - 1);
        currentDataWithTime.setSeconds(0);
        currentDataWithTime.setMilliseconds(0);

        this.state = {
            orderData: undefined,
            response: undefined,
            currentPage: 0,
            menuIsOpen: false,
            openMenuId: null,
            anchorMenu: null,
            isTableLoader: false,
            selectedDateOfRegistrationFrom: new ValidatedValue(currentDataWithTime),
            selectedDateOfRegistrationTo: new ValidatedValue(null),
            selectedArrivalDepartureTime: new ValidatedValue(null),
            selectedFerry: new ValidatedValue(""),
            selectedOrderStatus: new ValidatedValue(""),
            selectedRelationType: new ValidatedValue(""),
            displayPopup: false,
            orderBy: undefined,
            sortDirection: undefined,
            downloadFileResponse: undefined,
            cancelOrderResponse: undefined,
            isLoading: false,
            searchInput: new ValidatedValue(""),
            isSearchLoader: false
        };
    }

    private get orderData() {
        return this.state.orderData;
    }

    public async componentDidMount() {
        await this.getAllOrders(false);
    }

    public render(): JSX.Element {
        const properties = ["status", "shipownerName", "ferry", "arrivalDepartureTime", "relationType", "trailerNo",
            "regNo", "info"];
        const headers = properties.map(key => i18n.t("transportation.order.headers." + key));
        const actions = this.renderActions;

        return (
            <div className="transportation-order">
                {this.renderWrapperContent(headers, properties, "transportation-order", actions, this.renderCell)}
            </div>
        );
    }

    private renderWrapperContent(
        headers: string[], properties: string[], testId: string, actions: TableActionFunction,
        renderCell: (row: any, property: string) => JSX.Element
    ): JSX.Element {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={i18n.t("transportation.order.retrievalErrorSuffix")}
                />
            );
        } else {
            if (this.orderData === undefined || this.state.isLoading) {
                return <Loader/>;
            } else {
                return (
                    <>
                        {this.renderHeader()}
                        <div className="transportation-order-content">
                            {this.orderData && this.orderData!.length && this.orderData!.length > 0 ? (
                                <Table
                                    headers={headers}
                                    rows={this.state.orderData!}
                                    properties={properties}
                                    testId={`${testId}-table`}
                                    actions={actions ? actions.bind(this) : actions}
                                    renderCell={renderCell.bind(this)}
                                    pageable={true}
                                    currentPage={this.state.currentPage}
                                    onPageChange={this.getAllOrders.bind(this, false)}
                                    isOldPagination={false}
                                    isTableLoader={this.state.isTableLoader}
                                    sortableElements={this.sortableElements}
                                    sortingAction={this.sortingAction.bind(this)}
                                    orderBy={this.state.orderBy}
                                    sortDirection={this.state.sortDirection}
                                />
                            ) : (
                                <Table
                                    headers={headers}
                                    rows={[]}
                                    properties={properties}
                                    testId={`${testId}-table`}
                                    currentPage={0}
                                    numberOfElements={0}
                                />
                            )}
                            {this.renderWarningPopup()}
                            {this.renderFileDownloadErrorMessage()}
                            {this.renderCancelOrderErrorMessage()}
                        </div>
                    </>
                );
            }
        }
    }

    private renderHeader(): JSX.Element {
        const userStore = this.context.userStore as IUserStore;

        return (
            <div className="header">
                <div className="filters-content">
                    <TransportationFilters
                        element="order"
                        selectedDateOfRegistrationFrom={this.state.selectedDateOfRegistrationFrom}
                        selectedDateOfRegistrationTo={this.state.selectedDateOfRegistrationTo}
                        selectedArrivalDepartureTime={this.state.selectedArrivalDepartureTime}
                        selectedFerry={this.state.selectedFerry}
                        selectedOrderStatus={this.state.selectedOrderStatus}
                        selectedRelationType={this.state.selectedRelationType}
                        onDateTimePickerChange={(newDate: Date | null, isDateFrom: boolean, isDateTo: boolean) => {
                            this.dateTimePickerOnChange(newDate, isDateFrom, isDateTo);
                        }}
                        setIsLoading={(value: boolean) => {
                            this.setState({isLoading: value});
                        }}
                        searchInput={this.state.searchInput}
                    />
                </div>
                <div className="actions">
                    <SearchButton
                        onClick={async () => {
                            if (this.state.selectedDateOfRegistrationFrom.value || this.state.selectedDateOfRegistrationTo.value) {
                                await this.whetherToRunFiltering();
                            }
                        }}
                        isWaiting={this.state.isSearchLoader}
                    />
                    {userStore.canModifyOrder && (
                        <NavLink to={routingAddresses.order.add} data-testid="add-order">
                            <Button type="primary" text={i18n.t("transportation.order.actions.addOrder")}/>
                        </NavLink>
                    )}
                    <ReloadButton
                        reloadFunc={this.reload.bind(this)}
                        testId="order-list-reload-btn"
                    />
                </div>
            </div>
        );
    }

    private renderCell(row: any, property: string): JSX.Element {
        switch (property) {
            case "status": {
                const statusName = i18n.t(`orderStatus.${row[property]}`);
                return (
                    <Tooltip title={ statusName || ""} arrow>
                        <Chip
                            className={`${row[property]}`.toLocaleLowerCase()}
                            label={statusName}
                        />
                    </Tooltip>
                );
            }
            case "relationType":
                return <>{i18n.t(`relationType.${row[property]}`)}</>;
            case "arrivalDepartureTime":
                return <>{DateUtils.getFormattedDateWithTime(row[property])}</>;
            case "info":
                return <>{TransportUtils.renderInfoIcons(row)}</>;
            case "ferry":
                return <>{row[property]?.name || ""}</>;
            default:
                return row[property];
        }
    }

    private renderActions(element: OrderModel): JSX.Element {
        // @ts-ignore
        const elementId = element.id;
        const userStore = this.context.userStore as IUserStore;
        const isOnlyTcr = userStore.canModifyTcr;

        return (
            <>
                {elementId && (
                    <>
                        {this.renderDetailsAction(elementId)}
                        {isOnlyTcr ? !TransportUtils.isRelationTypeWithoutDepo(element.relationType) && this.renderModifyTcr(elementId) : this.renderMoreMenuAction(elementId, TransportUtils.isRelationTypeWithoutDepo(element.relationType))}
                    </>
                )}
            </>
        );
    }

    private renderDetailsAction(elementId: string): JSX.Element {
        return (
            <NavLink
                to={`${routingAddresses.order.details}/${elementId}`}
                className="action-button"
                data-testid={`preview-${elementId}`}
            >
                {i18n.t("preview")}
            </NavLink>
        );
    }

    private renderMoreMenuAction(elementId: string, isRelationTypeWithoutDepo: boolean): JSX.Element {
        const userStore = this.context.userStore as IUserStore;
        const canModifyOrder = userStore.canModifyOrder;

        return (
            <>
                <span
                    className="action-button more"
                    data-testid={`more-menu-${elementId}`}
                    onClick={(event) => {
                        this.handleToggleMoreMenu(event, elementId);
                    }}
                >
                    {i18n.t("more")}
                    <ExpandMoreIcon fontSize="small" className="more"/>
                </span>
                <PopperMenu
                    isOpen={this.state.menuIsOpen && this.state.openMenuId === elementId}
                    setOpen={(isOpen) => {
                        this.setState({menuIsOpen: isOpen});
                    }}
                    anchorEl={this.state.anchorMenu}
                    doNotCloseAt="more"
                    testId={`popper-menu-${this.state.openMenuId}`}
                >
                    {!isRelationTypeWithoutDepo && (<MenuItem className="menu-item-wrapper">
                        <NavLink
                            to={`${routingAddresses.order.tcr}/${this.state.openMenuId}`}
                            data-testid={`more-tcr-${this.state.openMenuId}`}
                            className="menu-item"
                        >
                            {i18n.t("transportation.order.actions.tcrLong")}
                        </NavLink>
                    </MenuItem>)}
                    <DownloadPrintButton
                        orderId={this.state.openMenuId}
                        orderData={this.orderData?.find(element => element.id === this.state.openMenuId)}
                        isFileDownloadError={(response: AxiosResponse<HttpErrorModel[] | any> | undefined) => {
                            this.setState({downloadFileResponse: response});
                        }}
                    />
                    {canModifyOrder && (
                        <MenuItem className="menu-item-wrapper">
                            <NavLink
                                to={`${routingAddresses.order.modify}/${this.state.openMenuId}`}
                                data-testid={`more-modify-${this.state.openMenuId}`}
                                className="menu-item"
                            >
                                {i18n.t("transportation.order.actions.modifyOrder")}
                            </NavLink>
                        </MenuItem>
                    )}
                    {canModifyOrder && (
                        <MenuItem className="menu-item-wrapper">
                            <Button
                                text={i18n.t("cancel")}
                                type="secondary"
                                variant="text"
                                className="menu-item button-item cancel"
                                onClick={this.cancelOrder.bind(this, elementId)}
                                testId="cancel-button"
                            />
                        </MenuItem>
                    )}
                </PopperMenu>
            </>
        );
    }

    private renderModifyTcr(elementId: string): JSX.Element {
        return (
            <NavLink
                to={`${routingAddresses.order.modifyTcr}/${elementId}`}
                className="action-button"
                data-testid={`tcr-${elementId}`}
            >
                {i18n.t("transportation.order.actions.tcr")}
            </NavLink>
        );
    }

    protected renderWarningPopup() {
        return (
            <WarningFiltersPopup
                displayPopup={this.state.displayPopup}
                actionPopup={async () => {
                    await this.getAllOrders(true);
                    this.setState({displayPopup: false});
                }}
                closePopup={() => this.setState({displayPopup: false})}
            />
        );
    }

    private renderFileDownloadErrorMessage() {
        if (!Api.isSuccess(this.state.downloadFileResponse)) {
            return (
                <HttpError
                    response={this.state.downloadFileResponse}
                    cause={i18n.t("transportation.order.details.files.fileDownloadErrorSuffix")}
                    onClose={async() => await this.setState({downloadFileResponse: undefined})}
                />
            );
        }
    }

    private renderCancelOrderErrorMessage() {
        if (!Api.isSuccess(this.state.cancelOrderResponse)) {
            return (
                <HttpError
                    response={this.state.cancelOrderResponse}
                    cause={i18n.t("transportation.order.details.files.cancelOrderErrorSuffix")}
                    onClose={async() => await this.setState({cancelOrderResponse: undefined})}
                />
            );
        }
    }

    private handleToggleMoreMenu(event: React.MouseEvent<HTMLSpanElement, MouseEvent>, elementId: string) {
        if (elementId === this.state.openMenuId) {
            this.setState({menuIsOpen: false, openMenuId: null});
        } else {
            this.setState({menuIsOpen: true, anchorMenu: event.currentTarget, openMenuId: elementId});
        }
    }

    private dateTimePickerOnChange(newDate: Date | null, isDateFrom: boolean, isDateTo: boolean) {
        if (isDateFrom) {
            this.state.selectedDateOfRegistrationFrom.setValue(newDate);
        } else if(isDateTo) {
            this.state.selectedDateOfRegistrationTo.setValue(newDate);
        } else {
            this.state.selectedArrivalDepartureTime.setValue(newDate);
        }
        Validator.addValidatorsToDateTimePickerForOrdersAndTickets(this.state.selectedDateOfRegistrationFrom, this.state.selectedDateOfRegistrationTo, this.state.selectedArrivalDepartureTime);
        if (!this.state.selectedDateOfRegistrationFrom.value && !this.state.selectedDateOfRegistrationTo.value && !this.state.selectedArrivalDepartureTime.value) {
            this.setState({displayPopup: true});
        }
    }

    private async whetherToRunFiltering() {
        this.setState({isSearchLoader: true});
        if (TransportUtils.dateToIsGreaterThanTheMonthBefore(this.state.selectedDateOfRegistrationFrom, this.state.selectedDateOfRegistrationTo)) {
            this.setState({displayPopup: true});
        } else {
            await this.getAllOrders(true);
        }
        this.setState({isSearchLoader: false});
    }

    public get isFilterValid() {
        this.state.selectedDateOfRegistrationFrom.validate();
        this.state.selectedDateOfRegistrationTo.validate();
        this.state.selectedArrivalDepartureTime.validate();
        this.state.selectedFerry.validate();
        this.state.selectedOrderStatus.validate();
        this.state.selectedRelationType.validate();

        return this.state.selectedDateOfRegistrationFrom.isValid && this.state.selectedDateOfRegistrationTo.isValid &&
            this.state.selectedArrivalDepartureTime.isValid && this.state.selectedFerry.isValid &&
            this.state.selectedOrderStatus && this.state.selectedRelationType.isValid;
    }

    private async getAllOrders(isFilter: boolean) {
        const paginationStore = this.context.paginationStore as IPaginationStore;
        const selectedDateOfRegistrationFrom = this.state.selectedDateOfRegistrationFrom.value;
        const selectedDateOfRegistrationTo = this.state.selectedDateOfRegistrationTo.value;
        const selectedArrivalDepartureTime = this.state.selectedArrivalDepartureTime.value;
        const dateToIsGreaterThanTheMonthBefore = TransportUtils.dateToIsGreaterThanTheMonthBefore(
            this.state.selectedDateOfRegistrationFrom, this.state.selectedDateOfRegistrationTo);

        this.setState({response: undefined, orderData: undefined, isTableLoader: true});

        if (!this.isFilterValid) {
            return;
        }

        if (!selectedDateOfRegistrationFrom && !selectedDateOfRegistrationTo && !this.state.displayPopup) {
            this.setState({displayPopup: true, orderData: []});
        } else if (dateToIsGreaterThanTheMonthBefore && !this.state.displayPopup) {
            this.setState({displayPopup: true, orderData: []});
        } else {
            const filters = {
                dateOfRegistrationFrom: DateUtils.getFormattedDateWithTimeInUTC(selectedDateOfRegistrationFrom),
                dateOfRegistrationTo: DateUtils.getFormattedDateWithTimeInUTC(selectedDateOfRegistrationTo),
                arrivalDepartureTime: DateUtils.getFormattedDateWithTimeInUTC(selectedArrivalDepartureTime),
                ferryEntityId: this.state.selectedFerry.value,
                relationType: this.state.selectedRelationType.value,
                status: this.state.selectedOrderStatus.value
            };

            const response = await OrderService.getAllOrders(
                filters,
                {
                    offset: isFilter ? 0 : paginationStore.offset,
                    size: paginationStore.size
                },
                {
                    orderBy: this.state.orderBy,
                    sortDirection: this.state.sortDirection
                },
                this.state.searchInput.value.trim()
            );

            this.setState({
                orderData: response?.data?.data || null,
                response,
                currentPage: response?.data?.currentPage,
                isTableLoader: false,
                displayPopup: false
            });

            paginationStore.setNumberOfElements(response?.data?.numberOfElements);
            paginationStore.setNumberOfPages(response?.data?.numberOfPages);
        }
    }

    private async reload() {
        this.setState({orderData: undefined});
        await this.getAllOrders(false);
    }

    private async sortingAction(orderBy: string, sortDirection: SortDirection) {
        if (orderBy === "ferry") {
            orderBy = "ferry.name";
        }

        await this.setState({orderBy, sortDirection});
        await this.getAllOrders(false);
    }

    private async cancelOrder(id: string) {
        this.setState({cancelOrderResponse: undefined});

        const cancelResponse = await OrderService.cancelOrder(id);

        this.setState({cancelOrderResponse: cancelResponse});

        if (Api.isSuccess(cancelResponse)) {
            this.reload();
        }
    }
}
